/**
 * Fieldworks bought from the budget after the garrison is in place: minefield patches,
 * wire runs, proximity posts, searchlights, alarm posts, a barracks. Mines never go on a
 * road, a ravine, a reserved crossing or a patrol's beat, so no band is ever sealed by them
 * (03 §5.1 step 7).
 */

import type { Pcg32 } from '../sim/rng';
import { NO_BUILD } from './builder';
import { FLAG } from './grid';
import { lampOverlooksQuietApproach } from './quietSide';
import { COST, MINES } from './tuning';
import type { GenCtx } from './types';

const NO_MINES = NO_BUILD | FLAG.path;

function bandSpan(ctx: GenCtx, band: number): { z0: number; z1: number } | null {
  const b = ctx.bands[band];
  if (!b) return null;
  // Only the northern part of the drop band: the ranger must have somewhere fair to land.
  if (b.kind === 'drop') return { z0: b.z0 + 4, z1: b.z0 + (b.z1 - b.z0) * 0.5 };
  // The border strip itself (Diversion) stays clear: that is where the Osprey comes.
  if (b.kind === 'goal') return { z0: ctx.spec.layout === 'border' ? ctx.plan.compound.z0 : 4, z1: b.z1 - 2 };
  return { z0: b.z0 + 2, z1: b.z1 - 2 };
}

export function mineDensity(ctx: GenCtx, base: number): number {
  return ctx.params.modifiers.includes('dense-minefields') ? base * MINES.denseFactor : base;
}

/**
 * Marked fields show their tell to everyone; from `MINES.hiddenFrom` some are left for intel or a
 * perk to find. Below that threat nothing is ever hidden, which is why the briefing stops offering
 * Sapper's Sketch there (`intelFor`) instead of selling a card with nothing to do (R6).
 */
export function minesMarked(ctx: GenCtx, rng: Pcg32): boolean {
  if (ctx.params.intel.includes('sappers-sketch')) return true;
  return !(ctx.params.threat >= MINES.hiddenFrom && rng.chance(0.3));
}

export function buyMinefield(ctx: GenCtx, rng: Pcg32, band: number, points: number): number {
  const span = bandSpan(ctx, band);
  if (!span || COST.minefield > points) return 0;
  const { player, pickup, compound: c } = ctx.plan;
  for (let tries = 0; tries < 24; tries++) {
    const turned = rng.chance(0.3);
    const w = turned ? MINES.patch.d : MINES.patch.w;
    const d = turned ? MINES.patch.w : MINES.patch.d;
    if (span.z1 - span.z0 < d + 2) return 0;
    const x0 = Math.round(rng.range(3, ctx.sizeX - 3 - w));
    const z0 = Math.round(rng.range(span.z0, span.z1 - d));
    // Never inside a compound, by the start or by the pickup.
    const inCompound = x0 + w > c.x0 - 2 && x0 < c.x1 + 2 && z0 + d > c.z0 - 2 && z0 < c.z1 + 2;
    if (inCompound && ctx.spec.layout === 'compound') continue;
    const near = (p: { x: number; z: number }, r: number): boolean =>
      p.x > x0 - r && p.x < x0 + w + r && p.z > z0 - r && p.z < z0 + d + r;
    if (near(player, 14) || near(pickup, 8)) continue;
    if (ctx.map.mask.hitsRect(x0 - 1, z0 - 1, x0 + w + 1, z0 + d + 1, NO_MINES)) continue;
    ctx.map.mineField({ x0, z0, x1: x0 + w, z1: z0 + d }, mineDensity(ctx, MINES.density), minesMarked(ctx, rng));
    return COST.minefield;
  }
  return 0;
}

export function buyWireRun(ctx: GenCtx, rng: Pcg32, band: number, points: number): number {
  const span = bandSpan(ctx, band);
  if (!span || COST.wireRun > points) return 0;
  for (let tries = 0; tries < 16; tries++) {
    const length = Math.round(rng.range(8, 14));
    const x = Math.round(rng.range(4 + length / 2, ctx.sizeX - 4 - length / 2));
    const z = Math.round(rng.range(span.z0 + 1, span.z1 - 1));
    if (ctx.map.place('wire', x, z, { length, margin: 2.2, avoid: NO_MINES })) return COST.wireRun;
  }
  return 0;
}

/** A bomb on a post beside a crossing - close enough to punish a careless rush, clear of the patrols. */
export function buyProximityPost(ctx: GenCtx, rng: Pcg32, band: number, points: number): number {
  if (COST.proximityPost > points) return 0;
  const cover = ctx.coverPoints.filter((p) => p.band === band || p.band === band + 1);
  for (let tries = 0; tries < 12 && cover.length > 0; tries++) {
    const at = rng.pick(cover);
    const x = Math.round(at.x + rng.range(5, 8) * (rng.chance(0.5) ? 1 : -1));
    const z = Math.round(at.z - rng.range(1.5, 6));
    if (ctx.map.mask.hitsCircle(x, z, 3.5, FLAG.road | FLAG.path | FLAG.keep)) continue;
    if (ctx.map.place('proximityPost', x, z, { margin: 1 })) return COST.proximityPost;
  }
  return 0;
}

function placeNear(
  ctx: GenCtx, rng: Pcg32, kind: 'searchlight' | 'alarmPost', x: number, z: number, spread: number,
): boolean {
  for (let tries = 0; tries < 10; tries++) {
    const px = Math.round(x + rng.range(-spread, spread));
    const pz = Math.round(z + rng.range(-spread, spread));
    // A lamp sweeping the open end of the quiet way in would shut it: there is no cover on it to wait behind.
    if (kind === 'searchlight' && lampOverlooksQuietApproach(ctx, px, pz)) continue;
    if (ctx.map.place(kind, px, pz, { margin: 0.8, avoid: NO_MINES })) return true;
  }
  return false;
}

export function buySearchlight(ctx: GenCtx, rng: Pcg32, band: number, points: number): number {
  if (COST.searchlight > points) return 0;
  const cover = ctx.coverPoints.filter((p) => p.band === band);
  if (cover.length === 0) return 0;
  const at = rng.pick(cover);
  return placeNear(ctx, rng, 'searchlight', at.x + (rng.chance(0.5) ? 7 : -7), at.z - 6, 3) ? COST.searchlight : 0;
}

/** An alerted soldier who reaches one raises the alarm (02 §9.2): put them where soldiers are. */
export function buyAlarmPost(ctx: GenCtx, rng: Pcg32, band: number, points: number): number {
  if (COST.alarmPost > points || ctx.map.props.filter((p) => p.kind === 'alarmPost').length >= 3) return 0;
  const span = bandSpan(ctx, band);
  const men = ctx.map.spawns.filter((s) => span && s.z >= span.z0 && s.z <= span.z1 && s.dormantUntil === undefined);
  if (men.length === 0) return 0;
  const by = rng.pick(men);
  return placeNear(ctx, rng, 'alarmPost', by.x, by.z, 5) ? COST.alarmPost : 0;
}

/** Reinforcements come from here while it stands (02 §9.3). One per map, in or just south of the compound. */
export function buyBarracks(ctx: GenCtx, rng: Pcg32, points: number): number {
  if (COST.barracks > points || ctx.map.props.some((p) => p.kind === 'barracks')) return 0;
  const c = ctx.plan.compound;
  for (let tries = 0; tries < 30; tries++) {
    const inside = tries < 18 && ctx.spec.patrolLoop === 'inside' && ctx.spec.layout === 'compound';
    const x = Math.round(rng.range(inside ? c.x0 + 9 : 10, inside ? c.x1 - 9 : ctx.sizeX - 10));
    const z = Math.round(inside ? rng.range(c.z0 + 6, c.z1 - 6) : c.z1 + rng.range(8, 14));
    if (ctx.map.place('barracks', x, z, { margin: 1.6, avoid: NO_MINES })) return COST.barracks;
  }
  return 0;
}

/** Free dressing that still matters: tank traps flanking the road at the belt, a watchtower by the wire. */
export function dressBelt(ctx: GenCtx): void {
  const rng = ctx.rng.fork('belt-dressing');
  const wire = ctx.plan.lines[ctx.plan.lines.length - 1];
  if (!wire) return;
  if (ctx.kit.tankTraps) {
    for (const side of [-1, 1]) {
      for (let i = 0; i < 3; i++) {
        const x = wire.roadX + side * (5.5 + i * 3.4);
        ctx.map.place('tankTrap', x, wire.z + 4 + rng.range(-0.6, 0.6), { margin: 0.9, avoid: NO_MINES });
      }
    }
  }
  if (ctx.params.threat >= 3) {
    const x = Math.round(wire.roadX + (wire.roadX < ctx.sizeX / 2 ? 9 : -9));
    ctx.map.place('watchtower', x, Math.round(wire.z - 6), { margin: 1.2, avoid: NO_MINES });
  }
}
